import { createApi } from '@reduxjs/toolkit/query/react';
import { customFetchBase } from './helpers/customFetchBase.js';
import {
  type BaseFeedback,
  type Feedback,
  type FeedbackStatus,
  type FeedbackVoteRequest,
  type FeedbackVoter,
  type UpdateFeedbackStatus,
  type UpdateFeedbackText,
} from '@soulhx/fs-common';
import { providesList } from './helpers/rtk-helpers.js';

export interface GetFeedbackParams {
  page?: number;
  status?: FeedbackStatus | FeedbackStatus[];
  sortByVotes?: boolean;
}

export interface SearchFeedbackParams {
  page?: number;
  status?: FeedbackStatus | FeedbackStatus[];
  sortByVotes?: boolean;
  searchText?: string;
}

export interface DeleteFeedbackResponse {
  success: boolean;
}

export interface FeedbackVoteParams extends FeedbackVoteRequest {
  feedbackId: string;
}

export const feedbackApi = createApi({
  reducerPath: 'feedback',
  baseQuery: customFetchBase,
  tagTypes: ['feedback', 'feedbackVoters'],
  endpoints: (builder) => ({
    newFeedback: builder.mutation<Feedback, BaseFeedback>({
      query: (body) => ({
        url: '/comms/feedback',
        body: body,
        method: 'POST',
      }),
      invalidatesTags: ['feedback'],
    }),
    getAllFeedback: builder.query<Feedback[], GetFeedbackParams>({
      query: (params) => {
        const queryParams: string[] = [];
        if (params.page) queryParams.push(`p=${params.page}`);
        if (params.status) {
          const statusList = Array.isArray(params.status) ? params.status.join(',') : params.status;
          queryParams.push(`status=${statusList}`);
        }
        if (params.sortByVotes) queryParams.push('sort=votes');

        return `/comms/feedback${queryParams.length ? '?' + queryParams.join('&') : ''}`;
      },
      providesTags: (result) => providesList(result, 'feedback'),
    }),
    getFeedbackById: builder.query<Feedback, string>({
      query: (id) => `/comms/feedback/${id}`,
      providesTags: (result) => (result ? providesList([result], 'feedback', { returnList: false }) : []),
    }),
    updateFeedback: builder.mutation<Feedback, UpdateFeedbackText>({
      query: (item) => ({
        url: `/comms/feedback/${item.id}`,
        method: 'PUT',
        body: item,
      }),
      invalidatesTags: ['feedback'],
    }),
    updateFeedbackStatus: builder.mutation<Feedback, UpdateFeedbackStatus>({
      query: (item) => ({
        url: `/comms/feedback/${item.id}/status`,
        method: 'PATCH',
        body: item,
      }),
      invalidatesTags: ['feedback'],
    }),
    deleteFeedback: builder.mutation<DeleteFeedbackResponse, string>({
      query: (id) => ({ url: `/comms/feedback/${id}`, method: 'DELETE' }),
      invalidatesTags: ['feedback'],
    }),
    markFeedbackReadForAdmin: builder.mutation<Feedback, string>({
      query: (id) => ({
        url: `/comms/feedback/${id}/read`,
        method: 'PATCH',
      }),
      invalidatesTags: ['feedback'],
    }),
    getUnreadFeedbackForAdmin: builder.query<Feedback[], void>({
      query: () => '/comms/feedback/adminUnread',
      providesTags: (result) => providesList(result, 'feedback'),
    }),
    searchFeedback: builder.mutation<Feedback[], SearchFeedbackParams>({
      query: (params) => {
        const queryParams: string[] = [];
        if (params.page) queryParams.push(`p=${params.page}`);
        if (params.status) {
          const statusList = Array.isArray(params.status) ? params.status.join(',') : params.status;
          queryParams.push(`status=${statusList}`);
        }
        if (params.sortByVotes) queryParams.push('sort=votes');

        return {
          url: `/comms/feedback/search${queryParams.length ? '?' + queryParams.join('&') : ''}`,
          body: { searchText: params.searchText },
          method: 'POST',
        };
      },
    }),
    upvoteFeedback: builder.mutation<Feedback, FeedbackVoteParams>({
      query: ({ feedbackId, ...body }) => ({
        url: `/comms/feedback/${feedbackId}/upvote`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['feedback', 'feedbackVoters'],
    }),
    removeFeedbackUpvote: builder.mutation<Feedback, FeedbackVoteParams>({
      query: ({ feedbackId, ...body }) => ({
        url: `/comms/feedback/${feedbackId}/upvote`,
        method: 'DELETE',
        body,
      }),
      invalidatesTags: ['feedback', 'feedbackVoters'],
    }),
    getFeedbackVoters: builder.query<FeedbackVoter[], string>({
      query: (feedbackId) => `/comms/feedback/${feedbackId}/upvotes`,
      providesTags: ['feedbackVoters'],
    }),
  }),
});

export const {
  useNewFeedbackMutation,
  useGetAllFeedbackQuery,
  useLazyGetAllFeedbackQuery,
  useGetFeedbackByIdQuery,
  useUpdateFeedbackMutation,
  useUpdateFeedbackStatusMutation,
  useDeleteFeedbackMutation,
  useMarkFeedbackReadForAdminMutation,
  useGetUnreadFeedbackForAdminQuery,
  useSearchFeedbackMutation,
  useUpvoteFeedbackMutation,
  useRemoveFeedbackUpvoteMutation,
  useGetFeedbackVotersQuery,
} = feedbackApi;
